import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, Cloud, Sparkles, TrendingUp, AlertTriangle,
  Gift, ChevronRight, Loader2, MapPin, BarChart3,
} from "lucide-react";
import TrendModal from "@/components/TrendModal";
import { trackPageView } from "@/lib/track";

const DigestCard = ({ offer, onClick, showGraph, onGraphClick, dealStatus }) => (
  <div className="w-full flex items-center gap-3 p-3 rounded-xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow text-left">
    <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
      <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 bg-slate-100 flex items-center justify-center">
        {offer.logo_url ? <img src={offer.logo_url} alt="" className="w-full h-full object-contain" /> : <Gift className="w-5 h-5 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-semibold text-slate-800 truncate">{offer.name}</p>
          {dealStatus && <UnifiedStatusBadge status={dealStatus} />}
        </div>
        <p className="text-xs text-slate-400 truncate">{offer.offer_title || offer.category}</p>
      </div>
      <div className="text-right flex-shrink-0">
        {offer.offer_value_est > 0 && <p className="text-sm font-bold text-green-600">${offer.offer_value_est}</p>}
        {offer.claim_count > 0 && <p className="text-[10px] text-emerald-600 font-medium">{offer.claim_count} claimed</p>}
        {offer.view_count > 0 && <p className="text-[10px] text-slate-400 font-medium">{offer.view_count} views</p>}
        {offer.degradation_reports > 0 && <p className="text-[10px] text-red-500 font-medium">{offer.degradation_reports} report(s)</p>}
      </div>
    </button>
    {showGraph ? (
      <button onClick={onGraphClick} className="p-2 rounded-lg bg-pink-50 hover:bg-pink-100 transition-colors flex-shrink-0" data-testid={`trend-btn-${offer.business_id}`}>
        <BarChart3 className="w-4 h-4 text-pink-500" />
      </button>
    ) : (
      <button onClick={onClick} className="flex-shrink-0"><ChevronRight className="w-4 h-4 text-slate-300" /></button>
    )}
  </div>
);

const Section = ({ icon: Icon, iconBg, title, count, emptyText, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 px-1">
      <span className={`w-7 h-7 rounded-lg ${iconBg} flex items-center justify-center`}><Icon className="w-3.5 h-3.5" /></span>
      <span className="text-sm font-bold text-slate-700">{title}</span>
      {count > 0 && <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{count}</span>}
    </div>
    {count > 0 ? children : <p className="text-xs text-slate-400 px-1 py-2">{emptyText}</p>}
  </div>
);

export default function WeatherReportPage() {
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const { geo } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trendTarget, setTrendTarget] = useState(null); // { business_id, name }

  useEffect(() => {
    trackPageView("weather_report");
    (async () => {
      try {
        let url = `${API}/digest/weather-report`;
        const params = [];
        if (geo?.coords) {
          params.push(`lat=${geo.coords.lat}`, `lng=${geo.coords.lng}`);
        }
        if (params.length) url += `?${params.join("&")}`;
        const res = await axios.get(url);
        setData(res.data);
      } catch (e) { console.error("Weather report error:", e); }
      finally { setLoading(false); }
    })();
  }, [geo?.coords]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><p className="text-slate-400">Failed to load report</p></div>;

  const { summary, new_deals, trending, degraded, location_context, location_mode } = data;

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="weather-report-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="weather-back"><ArrowLeft className="w-5 h-5 text-slate-600" /></button>
        <Cloud className="w-5 h-5 text-sky-500" />
        <h1 className="text-base font-bold text-slate-800">Deal Weather Report</h1>
      </div>

      <div className="px-4 py-4 space-y-6">
        {/* Location context badge */}
        <div className="flex items-center gap-1.5 px-1">
          <MapPin className="w-3.5 h-3.5 text-sky-500" />
          <span className="text-xs text-slate-500 font-medium" data-testid="weather-location-context">
            {location_mode === "gps" ? "Using your GPS location" : `Showing results for ${location_context}`}
          </span>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-sky-50 rounded-xl p-3 text-center border border-sky-100">
            <p className="text-xl font-bold text-sky-700">{summary.total_active_deals}</p>
            <p className="text-[10px] text-sky-500 font-medium">Active Deals</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3 text-center border border-green-100">
            <p className="text-xl font-bold text-green-700">${summary.average_deal_value}</p>
            <p className="text-[10px] text-green-500 font-medium">Avg Value</p>
          </div>
          <button
            onClick={() => navigate("/trending-map")}
            className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100 hover:border-amber-300 hover:shadow-md transition-all relative group"
            data-testid="trending-map-link"
          >
            <p className="text-xl font-bold text-amber-700">{summary.trending_count}</p>
            <p className="text-[10px] text-amber-500 font-medium">Trending Near You</p>
            <MapPin className="w-3 h-3 text-amber-400 absolute top-1.5 right-1.5 group-hover:text-amber-600 transition-colors" />
          </button>
        </div>

        {/* Degraded Deals (P5 merged) — show first if any */}
        {degraded.length > 0 && (
          <Section icon={AlertTriangle} iconBg="bg-red-100 text-red-600" title="Deal Alerts" count={degraded.length} emptyText="">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
              <p className="text-xs text-red-600 font-medium">These deals have recent user reports about changes:</p>
              {degraded.map((d) => (
                <DigestCard key={d.business_id} offer={d} onClick={() => navigate(`/offer/${d.business_id}`)} dealStatus={getStatus(d.business_id)} />
              ))}
            </div>
          </Section>
        )}

        {/* New This Week */}
        <Section icon={Sparkles} iconBg="bg-violet-100 text-violet-600" title="New This Week" count={new_deals.length} emptyText="No new deals added this week">
          <div className="space-y-2">
            {new_deals.map((d) => <DigestCard key={d.business_id} offer={d} onClick={() => navigate(`/offer/${d.business_id}`)} dealStatus={getStatus(d.business_id)} />)}
          </div>
        </Section>

        {/* Trending (location-aware) */}
        <Section icon={TrendingUp} iconBg="bg-pink-100 text-pink-600" title={`Trending ${location_context ? `in ${location_context}` : ""}`} count={trending.length} emptyText="No trending deals in your area yet">
          <div className="space-y-2">
            {trending.map((d) => (
              <DigestCard
                key={d.business_id}
                offer={d}
                onClick={() => navigate(`/offer/${d.business_id}`)}
                showGraph
                onGraphClick={() => setTrendTarget({ business_id: d.business_id, name: d.name })}
              />
            ))}
          </div>
        </Section>
      </div>

      <TrendModal
        isOpen={!!trendTarget}
        onClose={() => setTrendTarget(null)}
        businessId={trendTarget?.business_id}
        businessName={trendTarget?.name}
      />

      <BottomNav />
    </div>
  );
}
